import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { HOST } from 'config/config';

/**
 * Fetch all criteria options
 */
export const useCriteriaOptions = () => {
  return useQuery({
    queryKey: ['criteriaOptions'],
    queryFn: async () => {
      const response = await axios.get(`${HOST}/api/certification-tool/criteria-options`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (this data rarely changes)
  });
};

/**
 * Fetch a single criteria option by ID
 */
export const useCriteriaOption = criteriaOptionId => {
  return useQuery({
    queryKey: ['criteriaOption', criteriaOptionId],
    queryFn: async () => {
      if (!criteriaOptionId) {
        throw new Error('Criteria Option ID is required');
      }
      const response = await axios.get(`${HOST}/api/certification-tool/criteria-options/${criteriaOptionId}`);
      return response.data;
    },
    enabled: !!criteriaOptionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Create a new criteria option (admin only)
 */
export const useCreateCriteriaOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async criteriaOptionData => {
      const response = await axios.post(`${HOST}/api/certification-tool/criteria-options`, criteriaOptionData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['criteriaOptions'] });
    },
  });
};

/**
 * Update a criteria option (admin only)
 */
export const useUpdateCriteriaOption = criteriaOptionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async patchDocument => {
      const response = await axios.patch(
        `${HOST}/api/certification-tool/criteria-options/${criteriaOptionId}`,
        patchDocument,
        {
          headers: {
            'Content-Type': 'application/json-patch+json',
          },
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['criteriaOptions'] });
      queryClient.invalidateQueries({ queryKey: ['criteriaOption', criteriaOptionId] });
    },
  });
};

/**
 * Delete a criteria option (admin only)
 */
export const useDeleteCriteriaOption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async criteriaOptionId => {
      await axios.delete(`${HOST}/api/certification-tool/criteria-options/${criteriaOptionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['criteriaOptions'] });
    },
  });
};
